import { useCallback, useEffect, useState } from 'react';

/**
 * Téléphones : on considère "smartphone" soit un appareil avec pointeur tactile et petite dimension,
 * soit tout viewport dont la plus petite dimension est très réduite (≤ 500px), ce qui couvre les cas
 * où `(pointer: coarse)` n'est pas rapporté correctement par le navigateur.
 */
export function isLikelyPhoneViewport(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const shortEdge = Math.min(window.innerWidth, window.innerHeight);
  if (coarse && shortEdge <= 640) return true;
  return shortEdge <= 500;
}

async function requestFullscreen(): Promise<void> {
  const el = document.documentElement;
  if (document.fullscreenElement) return;
  const anyEl = el as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  if (el.requestFullscreen) {
    await el.requestFullscreen();
  } else if (anyEl.webkitRequestFullscreen) {
    await anyEl.webkitRequestFullscreen();
  }
}

async function lockLandscape(): Promise<void> {
  const o = screen.orientation as ScreenOrientation & {
    lock?: (type: OrientationLockType) => Promise<void>;
  };
  if (typeof o?.lock === 'function') {
    await o.lock('landscape-primary');
  }
}

export async function enterPhoneImmersiveMode(): Promise<void> {
  try {
    await requestFullscreen();
  } catch {
    /* refus navigateur / iOS limité */
  }
  try {
    await lockLandscape();
  } catch {
    /* souvent refusé sans fullscreen ou sur Safari iOS */
  }
}

export function exitPhoneImmersiveMode(): void {
  if (document.fullscreenElement && document.exitFullscreen) {
    void document.exitFullscreen().catch(() => {});
  }
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* ignore */
  }
}

export function usePortraitOnPhone(): boolean {
  const [portrait, setPortrait] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(orientation: portrait)').matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(orientation: portrait)');
    const sync = () => setPortrait(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  return portrait;
}

export function usePhoneViewportFlag(): boolean {
  const [phone, setPhone] = useState(isLikelyPhoneViewport);

  useEffect(() => {
    const sync = () => setPhone(isLikelyPhoneViewport());
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return phone;
}

/**
 * Sur téléphone uniquement : au démontage du gameplay, quitte le plein écran et déverrouille l’orientation.
 */
export function useCleanupPhoneImmersiveOnUnmount(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    return () => {
      if (isLikelyPhoneViewport()) {
        exitPhoneImmersiveMode();
      }
    };
  }, [enabled]);
}

export function usePhoneImmersiveStart(): {
  showStartOverlay: boolean;
  dismissStartOverlay: () => void;
  onStartTap: () => void;
} {
  const isPhone = usePhoneViewportFlag();
  const [dismissed, setDismissed] = useState(false);

  const dismissStartOverlay = useCallback(() => setDismissed(true), []);

  const onStartTap = useCallback(() => {
    void enterPhoneImmersiveMode().finally(() => {
      setDismissed(true);
    });
  }, []);

  return {
    showStartOverlay: isPhone && !dismissed,
    dismissStartOverlay,
    onStartTap,
  };
}
